import { percentOfOrderUnitPrice, unitPriceFor } from '@xop/dashboard-shared';

import type { CatalogFeature, InvoiceProduct, ProductSelectionMode } from './products-table';

/**
 * Pure product-line list transforms for the invoice/quote generator, extracted
 * from `generate-invoice-form.tsx`'s add/remove/update/mode handlers. These
 * return a NEW products array (or product) and never touch React state — the
 * orchestrator keeps the side effects (clearing per-line validation errors,
 * pruning `priceInputs`). No money is rendered here (it only multiplies the
 * already-numeric seats × charge), so it's wall-neutral; it also lives under
 * the financial-only-allowlisted `generate-invoice-form/` tree.
 */

/** A fresh blank line, defaulted to manual entry with discounts applied. The
 * caller supplies the id (it was `Date.now()` at the call site — kept there so
 * this stays pure/testable). */
export function createProductLine(id: string): InvoiceProduct {
  return {
    id,
    partNumber: '',
    description: '',
    seats: 1,
    chargePerSeat: 0,
    amount: 0,
    selectionMode: 'manual',
    excludeFromDiscount: false, // Default: apply discounts
  };
}

/** Drop the line with the given id. */
export function removeProduct(products: InvoiceProduct[], productId: string): InvoiceProduct[] {
  return products.filter((p) => p.id !== productId);
}

/**
 * Set one field on a line. When `seats` or `chargePerSeat` changes, the
 * read-only per-line `amount` is recomputed (= seats × charge, coercing
 * non-numerics to 0) so the Amount cell and the totals memo stay consistent.
 */
export function updateProductField(
  products: InvoiceProduct[],
  productId: string,
  field: keyof InvoiceProduct,
  value: any,
): InvoiceProduct[] {
  return products.map((product) => {
    if (product.id !== productId) return product;
    const updated = { ...product, [field]: value };
    if (field === 'seats' || field === 'chargePerSeat') {
      updated.amount = (Number(updated.seats) || 0) * (Number(updated.chargePerSeat) || 0);
    }
    return updated;
  });
}

/**
 * Switch a line between manual entry and catalog selection. Always clears
 * whatever the previous mode had selected; switching into catalog mode also
 * clears the manual part-number/description (they'll be auto-filled from the
 * feature that gets picked).
 *
 * There were three modes until 2026-09-13 — the third picked off a separate
 * price list. The price list IS the catalogue now, so offering both would be
 * offering the same rows twice.
 */
export function withProductMode(
  products: InvoiceProduct[],
  productId: string,
  mode: ProductSelectionMode,
): InvoiceProduct[] {
  return products.map((product) => {
    if (product.id !== productId) return product;
    const next: InvoiceProduct = { ...product, selectionMode: mode };
    // Clear every source selection. Deleting the optional keys (rather than
    // assigning `undefined`) keeps this clean under exactOptionalPropertyTypes;
    // an absent key reads the same as undefined everywhere downstream.
    delete next.selectedProductId;
    delete next.selectedFeatureId;
    delete next.listPriceCents;
    // A line typed by hand is priced by hand: without the mode the resolver
    // below leaves it alone, and without the rate there is nothing to apply.
    delete next.pricingMode;
    delete next.percentRate;
    if (mode !== 'manual') {
      next.partNumber = '';
      next.description = '';
    }
    return next;
  });
}

/**
 * Fill a line from a catalogue FEATURE: part number, description AND unit
 * price, with the per-line amount recomputed from the seats already on the
 * row.
 *
 * The feature is COPIED, not referenced. An issued quote must keep the price
 * it quoted even after somebody reprices the catalogue, so the line stores its
 * own numbers; `selectedFeatureId` and `listPriceCents` are only a breadcrumb
 * saying where they came from and what the book said at the time.
 *
 * HOW the feature is priced is copied too (`pricingMode`, and the rate for a
 * `percent` one). An `included` feature is genuinely free and stays at 0. A
 * `percent` feature ("15% of order") also seeds at 0 here, because this line
 * has not met the rest of the order yet — `resolvePercentLines` below prices
 * it off the other lines and re-prices it every time they change. An UNPRICED
 * fixed feature seeds at 0 with the price left for the operator to type.
 */
export function applyCatalogFeature(
  products: InvoiceProduct[],
  productId: string,
  feature: CatalogFeature,
): InvoiceProduct[] {
  return products.map((product) => {
    if (product.id !== productId) return product;
    const chargePerSeat = unitPriceFor(feature);
    const next: InvoiceProduct = {
      ...product,
      selectionMode: 'catalog',
      selectedFeatureId: feature.id,
      partNumber: feature.part_number ?? '',
      description: feature.description ?? feature.name ?? '',
      chargePerSeat,
      // The book's integer cents, snapshotted next to the dollar price the
      // form works in. Both the command center and the reseller portal write
      // this key in the same position, so a line is comparable across the two
      // surfaces and the two copies of this file stay diffable.
      listPriceCents: feature.price_cents,
      pricingMode: feature.pricing_mode,
      amount: (Number(product.seats) || 0) * chargePerSeat,
    };
    // The rate travels only with a percentage line; on any other it would be
    // a stale number the resolver must not be tempted by. Deleting (rather
    // than assigning undefined) keeps the shape clean under
    // exactOptionalPropertyTypes.
    if (feature.pricing_mode === 'percent' && feature.percent_rate != null) {
      next.percentRate = feature.percent_rate;
    } else {
      delete next.percentRate;
    }
    return next;
  });
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Price every "N% of order" line off the rest of the document.
 *
 * The base is the subtotal of every line that is NOT itself a percentage —
 * fixed, manual and included ones, before any discount or tax — and each
 * percentage line charges `rate% × base` per unit, times its quantity. Two
 * percentage lines never compound: both read the same base.
 *
 * Pure and idempotent, so the form DERIVES its product list through this on
 * every render (`generate-invoice-form.tsx`) rather than re-running it in each
 * handler: change a quantity, add a line, remove one, reload a saved document
 * — the ESP line follows without any handler having to remember. crm-api runs
 * the same rule on save (`InvoiceTotals#resolve_percent_lines!`), so the
 * preview and the stored document agree to the cent.
 *
 * Returns the SAME array when there is no percentage line, so a `useMemo`
 * downstream does not churn on every keystroke.
 */
export function resolvePercentLines(products: InvoiceProduct[]): InvoiceProduct[] {
  if (!products.some((p) => p.pricingMode === 'percent')) return products;
  const base = products.reduce((sum, p) => {
    if (p.pricingMode === 'percent') return sum;
    const amount = Number(p.amount) || 0;
    return sum + (amount !== 0 ? amount : (Number(p.seats) || 0) * (Number(p.chargePerSeat) || 0));
  }, 0);
  return products.map((p) => {
    if (p.pricingMode !== 'percent') return p;
    const chargePerSeat = percentOfOrderUnitPrice(p.percentRate, base);
    const amount = round2((Number(p.seats) || 0) * chargePerSeat);
    if (p.chargePerSeat === chargePerSeat && p.amount === amount) return p;
    return { ...p, chargePerSeat, amount };
  });
}
